import React, { useState, useEffect, useMemo } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Link,
  Spinner,
  Toast,
  ToastTitle,
  Toaster,
  Tooltip,
  useId,
  useToastController,
} from "@fluentui/react-components";
import { ArrowDownload20Filled, Dismiss24Regular } from "@fluentui/react-icons";
import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
import moment from "moment";
import apiServices from "../../../service";
import { Job, JobChild } from "./jobManagementTypes";
import { resolveFilename, triggerDownload } from "./downloadUtils";

// ── constants ─────────────────────────────────────────────────────────────────

const JOB_TYPE_LABELS: Record<string, string> = {
  LETTER_GENERATION: "Letter",
  EMAIL_GENERATION: "Email",
  REPORT: "Report",
};

type BadgeColor = "success" | "danger" | "warning" | "informative" | "subtle";

const STATUS_BADGE: Record<string, { color: BadgeColor; label: string }> = {
  success: { color: "success", label: "Success" },
  failed: { color: "danger", label: "Failed" },
  partial_success: { color: "warning", label: "Partial Success" },
  in_progress: { color: "informative", label: "In Progress" },
  pending: { color: "subtle", label: "Pending" },
};

// ── sub-components ────────────────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = STATUS_BADGE[(status ?? "").toLowerCase()] ?? { color: "subtle" as BadgeColor, label: status };
  return (
    <Badge appearance="filled" color={cfg.color} size="medium">
      {cfg.label}
    </Badge>
  );
};

// ── props & component ─────────────────────────────────────────────────────────

interface Props {
  job: Job | null;
  open: boolean;
  onClose: () => void;
}

const JobDetailsModal: React.FC<Props> = ({ job, open, onClose }) => {
  // Ensure children is initialized safely as an empty array
  const [children, setChildren] = useState<JobChild[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

  const toasterId = useId("modal-toaster");
  const { dispatchToast } = useToastController(toasterId);
  const notifyError = (message: string) =>
    dispatchToast(<Toast><ToastTitle>{message}</ToastTitle></Toast>, { intent: "error" });

  // Child download: resolves the real filename from response headers (DOCX).
  const handleDownload = async (documentId: string) => {
    setDownloadingDocId(documentId);
    try {
      const res = await apiServices.jobManagement.download(documentId);
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
      const filename = await resolveFilename(blob, res.headers as Record<string, string>, documentId);
      triggerDownload(blob, filename);
    } catch {
      notifyError("Failed to download document.");
    } finally {
      setDownloadingDocId(null);
    }
  };

  // Parent download: always produces a ZIP (aggregated output for the whole job).
  const handleParentDownload = async (documentId: string) => {
    setDownloadingDocId(documentId);
    try {
      const res = await apiServices.jobManagement.download(documentId);
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: "application/zip" });
      triggerDownload(blob, `${documentId}.zip`);
    } catch {
      notifyError("Failed to download document.");
    } finally {
      setDownloadingDocId(null);
    }
  };

  // fetch children whenever the modal opens for a parent, non-REPORT job
  useEffect(() => {
    // Always fully reset transient state when modal closes or job changes so
    // stale data (loading spinner, download state, error messages, previous
    // children) is never visible when the modal re-opens for a different job.
    if (!open || !job) {
      setChildren([]);
      setFetchError(null);
      setLoading(false);
      setDownloadingDocId(null);
      return;
    }

    // REPORT jobs surface a zip URL instead of a children list.
    if (job.process_data?.type === "REPORT") {
      setChildren([]);
      return;
    }

    // Guard: the /children endpoint only accepts parent job IDs.
    // process_data.isParent is the authoritative flag; never call this API
    // with a child job ID (isParent === false) regardless of how the job
    // arrived in the listing.
    if (!job.process_data?.isParent) {
      setChildren([]);
      return;
    }

    let cancelled = false;
    setChildren([]);
    setFetchError(null);
    setLoading(true);

    apiServices.jobManagement
      .getJobChildren(job.jobId)
      .then((res) => {
        if (!cancelled) {
          const responseData = res?.data?.data ?? [];
          if (Array.isArray(responseData)) {
            const transformedData: JobChild[] = responseData.map((item: any) => ({
              customerFirstname: item.process_data?.first_name ?? "—",
              customerLastname: item.process_data?.last_name ?? "—",
              status: item.process_status ?? "PENDING",
              documentId: item.process_data?.documentId ?? null,
              error: item.error_details ?? null,
            }));
            setChildren(transformedData);
          } else {
            setChildren([]);
          }
        }
      })
      .catch(() => {
        if (!cancelled) setFetchError("Failed to load job details. Please try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [open, job]);

  // ── children table columns ───────────────────────────────────────────────────

  const childColumns = useMemo<MRT_ColumnDef<JobChild>[]>(
    () => [
      {
        accessorKey: "customerFirstname",
        header: "First Name",
        size: 120,
      },
      {
        accessorKey: "customerLastname",
        header: "Last Name",
        size: 120,
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 130,
        Cell: ({ cell }) => <StatusBadge status={cell.getValue<string>()} />,
      },
      {
        id: "action",
        header: "Action",
        size: 80,
        Cell: ({ row }) => {
          const docId = row.original.documentId;
          if (row.original.status?.toUpperCase() === "SUCCESS" && docId) {
            return (
              <Tooltip content="Download document" relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<ArrowDownload20Filled />}
                  aria-label="Download document"
                  disabled={downloadingDocId === docId}
                  onClick={() => handleDownload(docId)}
                />
              </Tooltip>
            );
          }
          return <span style={{ color: "#9aa0ae" }}>—</span>;
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [downloadingDocId]
  );

  if (!job) return null;

  const jobType = job.process_data?.type ?? "";
  const isReport = jobType === "REPORT";
  const isParentJob = job.process_data?.isParent === true;
  const typeLabel = JOB_TYPE_LABELS[jobType] ?? jobType;
  const dateSource = job.process_data?.completedAt ?? "";
  const formattedDate = dateSource ? moment(dateSource).format("DD/MM/YYYY") : "—";
  const formattedTime = dateSource ? moment(dateSource).format("HH:mm") : "—";

  // Optional chaining added to array filters for enhanced runtime safety
  const successCount = children?.filter((c) => c.status?.toUpperCase() === "SUCCESS")?.length ?? 0;
  const failedCount = children?.filter((c) => c.status?.toUpperCase() === "FAILED")?.length ?? 0;
  const pendingCount = children?.filter((c) => c.status?.toUpperCase() === "IN_PROGRESS" || c.status?.toUpperCase() === "PARTIAL_SUCCESS")?.length ?? 0;

  return (
    <>
      <Toaster toasterId={toasterId} />
      <Dialog
        open={open}
      // onOpenChange={(_, data) => { if (!data.open) onClose(); }}
      >
        <DialogSurface style={{ maxWidth: 820, width: "95vw", maxHeight: "92vh" }}>
          <DialogBody style={{ display: "flex", flexDirection: "column", gap: 0, maxHeight: "90vh" }}>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "2px 2px 12px 2px",
              }}
            >
              <div>
                <div style={{ fontSize: 18, fontWeight: 600 }}>
                  {job.consultant}
                </div>

                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {typeLabel} — {formattedDate} {formattedTime}
                </div>
              </div>

              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={onClose}
              />
            </div>

            <DialogContent style={{ paddingTop: 0, paddingBottom: 12, overflowY: "auto", flex: 1, maxHeight: "calc(90vh - 120px)" }}>

              {/* ── job meta summary ─────────────────────────────────────── */}
              <table className="jm-meta-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Consultant</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{formattedDate}</td>
                    <td>{formattedTime}</td>
                    <td>{typeLabel}</td>
                    <td><StatusBadge status={job.process_status} /></td>
                    <td>{job.consultant}</td>
                  </tr>
                </tbody>
              </table>

              {isReport ? (
                /* ── report: zip download ────────────────────────────────── */
                <div style={{ marginTop: 16 }}>
                  <Button
                    appearance="outline"
                    icon={<ArrowDownload20Filled />}
                    shape="rounded"
                    as="a"
                    href={job.process_data?.zipFileUrl ?? "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    disabled={!job.process_data?.zipFileUrl}
                  >
                    Download Report
                  </Button>
                </div>
              ) : !isParentJob ? (
                /* ── child / individual record ───────────────────────────── */
                <div style={{ marginTop: 16, padding: "12px 0", fontSize: 13, color: "#6b7280" }}>
                  This is an individual record — no sub-jobs to display.
                  {job.process_data?.documentId && (
                    <div style={{ marginTop: 12 }}>
                      <Tooltip content="Download document" relationship="label">
                        <Button
                          appearance="outline"
                          icon={<ArrowDownload20Filled />}
                          shape="rounded"
                          disabled={downloadingDocId === job.process_data.documentId}
                          onClick={() => handleDownload(job.process_data!.documentId!)}
                        >
                          {downloadingDocId === job.process_data.documentId ? "Downloading…" : "Download Document"}
                        </Button>
                      </Tooltip>
                    </div>
                  )}
                </div>
              ) : loading ? (
                <div className="jm-loading" style={{ padding: "32px 0" }}>
                  <Spinner size="small" label="Loading details..." />
                </div>
              ) : fetchError ? (
                <div className="jm-error">{fetchError}</div>
              ) : (
                <>
                  {/* ── count summary cards ─────────────────────────────── */}
                  <div className="jm-count-row">
                    <div className="jm-count-card cc-total">
                      <div className="jm-count-num">{children?.length ?? 0}</div>
                      <div className="jm-count-lbl">Total</div>
                    </div>
                    <div className="jm-count-card cc-success">
                      <div className="jm-count-num">{successCount}</div>
                      <div className="jm-count-lbl">Success</div>
                    </div>
                    <div className="jm-count-card cc-pending">
                      <div className="jm-count-num">{pendingCount}</div>
                      <div className="jm-count-lbl">Pending</div>
                    </div>
                    <div className="jm-count-card cc-failed">
                      <div className="jm-count-num">{failedCount}</div>
                      <div className="jm-count-lbl">Failed</div>
                    </div>
                    {job.process_data?.documentId && (
                      <Tooltip
                        content={downloadingDocId === job.process_data.documentId ? "Downloading…" : "Download ZIP"}
                        relationship="label"
                      >
                      <div
                        onClick={() => downloadingDocId !== job.process_data!.documentId && handleParentDownload(job.process_data!.documentId!)}
                        style={{
                          flex: 1,
                          borderRadius: 12,
                          background: downloadingDocId === job.process_data.documentId ? "#e5e7eb" : "#f3f4f6",
                          color: "#1d4ed8",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                          cursor: downloadingDocId === job.process_data.documentId ? "not-allowed" : "pointer",
                          transition: "all 0.2s ease",
                          gap: 8,
                          minHeight: 80,
                        }}
                      >
                        {downloadingDocId === job.process_data.documentId ? (
                          <>
                            <Spinner size="small" />
                            <div style={{ fontSize: 12, color: "#6b7280" }}>Downloading…</div>
                          </>
                        ) : (
                          <>
                            <ArrowDownload20Filled style={{ fontSize: 32 }} />
                            <div>Download</div>
                          </>
                        )}
                      </div>
                      </Tooltip>
                    )}
                  </div>

                  {/* ── child customer records table ─────────────────────── */}
                  <MaterialReactTable<JobChild>
                    columns={childColumns}
                    data={children ?? []}
                    enableColumnFilters={false}
                    enableGlobalFilter={false}
                    enableColumnActions={false}
                    enableFullScreenToggle={false}
                    enableDensityToggle={false}
                    enableHiding={false}
                    initialState={{
                      density: "compact",
                      pagination: { pageIndex: 0, pageSize: 10 },
                    }}
                    muiTableContainerProps={{ sx: { maxHeight: "260px" } }}
                    muiTablePaginationProps={{
                      rowsPerPageOptions: [10, 25],
                      showFirstButton: false,
                      showLastButton: false,
                    }}
                    muiToolbarAlertBannerProps={{ sx: { display: "none" } }}
                    muiTableBodyCellProps={() => ({
                      style: { padding: "6px 10px", fontSize: "0.8rem" },
                    })}
                  />
                </>
              )}
            </DialogContent>

          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default JobDetailsModal;